import { State } from '@adtech-web/twork-login-provider';
import React, { ReactNode, useContext } from 'react';
import { ICState } from 'schema/state';
import { addBreadcrumb, sendError as sendSentryError, SentryEventOptions } from '../sentry';

interface ErrorTrackingContextProps {
  children: ReactNode;
}

type SendErrorOptions = SentryEventOptions & {
  attachAuth?: boolean;
  state?: ICState;
};

type ErrorTrackingContextReturn = {
  sendError: (error: Error, options?: SendErrorOptions) => void;
};

const ErrorTrackingContext = React.createContext<ErrorTrackingContextReturn | undefined>(undefined);

export const ErrorTrackingContextProvider = ({ children }: ErrorTrackingContextProps) => {
  const sendError = (error: Error, { state, attachAuth, ...sentryOptions }: SendErrorOptions = {}) => {
    if (state) {
      addBreadcrumb({ level: 'debug', category: 'pollsIC', data: { pollsIc: state.pollsIc } });
    }

    if (attachAuth) {
      try {
        const tokenValue = getTokenFromStorageOrFail();

        addBreadcrumb({
          level: 'debug',
          category: 'auth',
          data: {
            token: {
              expiry: tokenValue.accessToken?.expiry,
              hasValue: !!tokenValue.accessToken?.value,
              hasAuthorizationCode: !!tokenValue.authorizationCode,
              hasAuthCodeBeenExchangedForAccessToken: tokenValue.hasAuthCodeBeenExchangedForAccessToken,
              scopes: tokenValue.scopes,
            },
          },
        });
      } catch {
        addBreadcrumb({
          level: 'debug',
          category: 'auth',
          data: { token: null },
        });
      }
    }

    sendSentryError(error, sentryOptions);
  };

  return <ErrorTrackingContext.Provider value={{ sendError }}>{children}</ErrorTrackingContext.Provider>;
};

export function useErrorTrackingContext() {
  const value = useContext(ErrorTrackingContext);
  if (!value) throw new Error('ErrorTrackingContext is required!');
  return value;
}

export function getTokenFromStorageOrFail() {
  const token = localStorage.getItem(`@adtech-web/twork-login-provider/${window.APP_TWORK_CLIENT_ID}`);

  if (!token) throw new Error('Token not found');

  return JSON.parse(token) as State;
}

export function clearTokenInStorage() {
  localStorage.removeItem(`@adtech-web/twork-login-provider/${window.APP_TWORK_CLIENT_ID}`);
}
